import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, ShoppingCart, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "buchen_wizard_v1";
const eur = (n) => `${(n || 0).toFixed(2).replace(".", ",")} €`;

const STEPS = ["Paket", "Termin", "Firma", "Kontakt", "Rechnung", "Buchen"];

export default function Buchen() {
  const [pakete, setPakete] = useState([]);
  const [ust, setUst] = useState(19);
  const [verf, setVerf] = useState({});
  const [step, setStep] = useState(0);
  const [cart, setCart] = useState([]);
  const [firma, setFirma] = useState({ firmenname: "", rechtsform: "", strasse: "", plz: "", ort: "", land: "DE", ustid: "", steuernummer: "", rechnungs_email: "", website: "", instagram: "", facebook: "", referenz: "" });
  const [abwAn, setAbwAn] = useState(false);
  const [rAnschrift, setRAnschrift] = useState({ supplement: "", street: "", zip: "", city: "", countryCode: "DE" });
  const [ap, setAp] = useState({ vorname: "", nachname: "", position: "", email: "", telefon: "" });
  const [cpAn, setCpAn] = useState(false);
  const [cp, setCp] = useState({ name: "", email: "", telefon: "" });
  const [sepa, setSepa] = useState(false);
  const [anmerkungen, setAnmerkungen] = useState("");
  const [consents, setConsents] = useState({ agb: false, datenschutz: false, rechte: false });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [idem] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY))?.idem || crypto.randomUUID(); } catch { return crypto.randomUUID(); }
  });

  useEffect(() => {
    axios.get(`${API}/public/booking/pakete`).then(({ data }) => { setPakete(data.pakete); setUst(data.ust_satz); });
    axios.get(`${API}/public/booking/verfuegbarkeit`).then(({ data }) => setVerf(data));
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY));
      if (saved) {
        setCart(saved.cart || []); setFirma((f) => ({ ...f, ...saved.firma })); setAp((a) => ({ ...a, ...saved.ap }));
        setStep(saved.step || 0); setSepa(saved.sepa || false);
      }
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({ idem, cart, firma, ap, step, sepa }));
  }, [idem, cart, firma, ap, step, sepa]);

  const summe = useMemo(() => {
    const netto = cart.reduce((s, c) => s + (pakete.find((p) => p.key === c.paket_key)?.preis_netto || 0), 0);
    const u = Math.round(netto * ust) / 100;
    return { netto, ust: u, brutto: Math.round((netto + u) * 100) / 100 };
  }, [cart, pakete, ust]);
  const aboGesamt = useMemo(() => cart.reduce((s, c) => {
    const p = pakete.find((x) => x.key === c.paket_key);
    return s + (p?.typ === "abo" ? p.preis_netto * (p.laufzeit_monate || 3) : (p?.preis_netto || 0));
  }, 0), [cart, pakete]);

  const addPaket = (key) => setCart((c) => [...c, { paket_key: key, startmonat: null, wunschdatum: null, formattyp: key === "B" ? "reel" : null, event: key === "C" ? { name: "", termin: "", alternativtermin: "", uhrzeit: "", ort: "", beschreibung: "" } : null }]);

  const terminOk = cart.every((c) => {
    const p = pakete.find((x) => x.key === c.paket_key);
    if (p?.typ === "abo") return !!c.startmonat;
    if (p?.typ === "einmalig") return !!c.wunschdatum && !!c.formattyp;
    return !!c.event?.name && !!c.event?.termin;
  });
  const firmaOk = firma.firmenname && firma.strasse && firma.plz && firma.ort && /\S+@\S+\.\S+/.test(firma.rechnungs_email);
  const apOk = ap.vorname && ap.nachname && /\S+@\S+\.\S+/.test(ap.email) && ap.telefon;
  const stepOk = [cart.length > 0, terminOk, firmaOk, apOk, true, consents.agb && consents.datenschutz && consents.rechte][step];

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/public/booking`, {
        idempotency_key: idem, warenkorb: cart,
        firma: { ...firma, rechnungsanschrift: abwAn ? rAnschrift : null },
        ansprechpartner: { ...ap, content_person: cpAn ? cp : null },
        sepa_interesse: sepa, anmerkungen, ...consents, firmenname_confirm: "",
      });
      localStorage.removeItem(LS_KEY);
      setDone(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Buchung fehlgeschlagen — bitte erneut versuchen");
    } finally { setSending(false); }
  };

  if (done) return (
    <Shell>
      <div className="text-center py-10" data-testid="booking-success">
        <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
        <h2 className="font-head text-2xl font-bold mt-4">Verbindlich gebucht — danke!</h2>
        <p className="text-neutral-500 mt-2">Deine Buchungsnummer: <b data-testid="booking-number">{done.buchungsnummer}</b></p>
        <p className="text-sm text-neutral-500 mt-2">Du bekommst in wenigen Minuten eine Bestätigungs-E-Mail mit allen Details und deinem persönlichen Upload-Link.</p>
        <a href={done.upload_url} data-testid="booking-upload-link" className="inline-block mt-6 bg-neutral-900 text-white font-bold px-6 py-3 rounded-xl">Jetzt Inhalte hochladen →</a>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="flex items-center gap-1.5 mb-6 overflow-x-auto" data-testid="wizard-progress">
        {STEPS.map((s, i) => (
          <div key={s} className={`flex items-center gap-1.5 shrink-0 ${i > 0 ? "ml-1" : ""}`}>
            <span className={`h-6 w-6 rounded-full text-[11px] font-bold flex items-center justify-center ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-neutral-900 text-white" : "bg-neutral-200 text-neutral-500"}`}>{i + 1}</span>
            <span className={`text-[11px] font-semibold ${i === step ? "text-neutral-900" : "text-neutral-400"}`}>{s}</span>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="space-y-4">
          <h2 className="font-head text-2xl font-bold">Wähle dein Paket</h2>
          {pakete.map((p) => {
            const naechster = (verf[p.key] || []).find((m) => !m.ausgebucht);
            return (
              <div key={p.key} className="rounded-2xl border border-neutral-200 bg-white p-5" data-testid={`paket-card-${p.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">{p.typ === "abo" ? "Fix / Abo · 3 Monate" : p.typ === "event" ? "Event-Reportage" : "Flex / Einmalig"}</p>
                    <h3 className="font-head text-lg font-bold mt-0.5">{p.name}</h3>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-head text-xl font-bold">{eur(p.preis_netto)}</p>
                    <p className="text-[11px] text-neutral-400">netto {p.typ === "abo" ? "pro Monat" : "pauschal"} · {eur(p.preis_brutto)} brutto</p>
                  </div>
                </div>
                <ul className="mt-3 space-y-1">{p.leistungen.map((l) => <li key={l} className="text-sm text-neutral-600 flex gap-2"><span className="text-emerald-600">✓</span>{l}</li>)}</ul>
                <p className="text-[11px] text-neutral-400 mt-2">{p.hinweis}{p.kontingent && naechster ? ` · Nächster freier Monat: ${naechster.label} (${naechster.frei} von ${naechster.limit} frei)` : ""}</p>
                <button data-testid={`paket-add-${p.key}`} onClick={() => addPaket(p.key)} className="mt-3 w-full bg-neutral-900 text-white text-sm font-bold py-2.5 rounded-xl">In den Warenkorb</button>
              </div>
            );
          })}
          {cart.length > 0 && (
            <div className="rounded-2xl bg-neutral-900 text-white p-4" data-testid="cart-summary">
              <p className="text-sm font-bold flex items-center gap-2"><ShoppingCart className="h-4 w-4" /> {cart.length} Paket(e) · {eur(summe.netto)} netto ({eur(summe.brutto)} brutto)</p>
              {cart.map((c, i) => (
                <div key={i} className="flex items-center justify-between mt-2 text-sm text-white/80">
                  <span>{pakete.find((p) => p.key === c.paket_key)?.name}</span>
                  <button data-testid={`cart-remove-${i}`} onClick={() => setCart((x) => x.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5">
          <h2 className="font-head text-2xl font-bold">Termin &amp; Zeitraum</h2>
          {cart.map((c, i) => {
            const p = pakete.find((x) => x.key === c.paket_key);
            const upd = (patch) => setCart((x) => x.map((y, j) => (j === i ? { ...y, ...patch } : y)));
            return (
              <div key={i} className="rounded-2xl border border-neutral-200 bg-white p-5">
                <h3 className="font-bold text-sm mb-3">{p?.name}</h3>
                {p?.typ === "abo" && (
                  <div>
                    <label className="text-xs font-semibold text-neutral-500">Startmonat (Laufzeit 3 Monate)</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                      {(verf.A || []).slice(0, 6).map((m) => {
                        const alle3frei = [0, 1, 2].every((k) => { const mm = (verf.A || [])[((verf.A || []).findIndex((x) => x.monat === m.monat)) + k]; return mm && !mm.ausgebucht; });
                        return (
                          <button key={m.monat} data-testid={`monat-${m.monat}`} disabled={!alle3frei} onClick={() => upd({ startmonat: m.monat })}
                            className={`rounded-xl border p-2.5 text-left ${c.startmonat === m.monat ? "border-neutral-900 bg-neutral-900 text-white" : alle3frei ? "border-neutral-200 bg-white" : "border-neutral-100 bg-neutral-50 opacity-50"}`}>
                            <p className="text-sm font-bold">{m.label}</p>
                            <p className={`text-[11px] ${c.startmonat === m.monat ? "text-white/70" : m.frei <= 1 ? "text-red-500" : "text-emerald-600"}`}>{m.ausgebucht || !alle3frei ? "ausgebucht" : `noch ${m.frei} von ${m.limit} Plätzen frei`}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {p?.typ === "einmalig" && (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      {["reel", "post"].map((f) => (
                        <button key={f} data-testid={`format-${f}-${i}`} onClick={() => upd({ formattyp: f })}
                          className={`flex-1 rounded-xl border py-2.5 text-sm font-bold capitalize ${c.formattyp === f ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200"}`}>{f}</button>
                      ))}
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-neutral-500">Wunsch-Veröffentlichungsdatum</label>
                      <input type="date" data-testid={`wunschdatum-${i}`} value={c.wunschdatum || ""} min={new Date().toISOString().slice(0, 10)} onChange={(e) => upd({ wunschdatum: e.target.value })} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200" />
                      {c.wunschdatum && (() => { const m = (verf.B || []).find((x) => x.monat === c.wunschdatum.slice(0, 7)); return m ? <p className={`text-[11px] mt-1 ${m.ausgebucht ? "text-red-500" : "text-emerald-600"}`}>{m.ausgebucht ? `${m.label} ist ausgebucht` : `${m.label}: noch ${m.frei} von ${m.limit} Plätzen frei`}</p> : null; })()}
                    </div>
                  </div>
                )}
                {p?.typ === "event" && (
                  <div className="grid gap-3">
                    {[["name", "Veranstaltungsname *"], ["ort", "Veranstaltungsort / Adresse"], ["uhrzeit", "Uhrzeit"], ["beschreibung", "Kurze Beschreibung des Anlasses"]].map(([k, label]) => (
                      <div key={k}><label className="text-xs font-semibold text-neutral-500">{label}</label>
                        <input data-testid={`event-${k}-${i}`} value={c.event?.[k] || ""} onChange={(e) => upd({ event: { ...c.event, [k]: e.target.value } })} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200" /></div>
                    ))}
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-xs font-semibold text-neutral-500">Wunschtermin *</label>
                        <input type="date" data-testid={`event-termin-${i}`} value={c.event?.termin || ""} onChange={(e) => upd({ event: { ...c.event, termin: e.target.value } })} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200" /></div>
                      <div><label className="text-xs font-semibold text-neutral-500">Alternativtermin</label>
                        <input type="date" value={c.event?.alternativtermin || ""} onChange={(e) => upd({ event: { ...c.event, alternativtermin: e.target.value } })} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200" /></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <h2 className="font-head text-2xl font-bold">Firmendaten</h2>
          <Feld label="Firmenname (exakter Rechnungsname) *" tid="firma-name" v={firma.firmenname} set={(v) => setFirma({ ...firma, firmenname: v })} />
          <Feld label="Rechtsform (optional)" tid="firma-rechtsform" v={firma.rechtsform} set={(v) => setFirma({ ...firma, rechtsform: v })} />
          <Feld label="Straße + Hausnummer *" tid="firma-strasse" v={firma.strasse} set={(v) => setFirma({ ...firma, strasse: v })} />
          <div className="grid grid-cols-3 gap-3">
            <Feld label="PLZ *" tid="firma-plz" v={firma.plz} set={(v) => setFirma({ ...firma, plz: v })} />
            <div className="col-span-2"><Feld label="Ort *" tid="firma-ort" v={firma.ort} set={(v) => setFirma({ ...firma, ort: v })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Feld label="USt-IdNr. (optional)" tid="firma-ustid" v={firma.ustid} set={(v) => setFirma({ ...firma, ustid: v })} ph="DE123456789" />
            <Feld label="Steuernummer (optional)" tid="firma-stnr" v={firma.steuernummer} set={(v) => setFirma({ ...firma, steuernummer: v })} />
          </div>
          {firma.ustid && !/^DE[0-9]{9}$/.test(firma.ustid) && <p className="text-[11px] text-red-500">Format: DE + 9 Ziffern</p>}
          <Feld label="Rechnungs-E-Mail *" tid="firma-remail" v={firma.rechnungs_email} set={(v) => setFirma({ ...firma, rechnungs_email: v })} ph={ap.email || "rechnung@firma.de"} />
          <button data-testid="toggle-rechnungsanschrift" onClick={() => setAbwAn(!abwAn)} className="text-xs font-bold text-neutral-500 underline">{abwAn ? "− Abweichende Rechnungsanschrift ausblenden" : "+ Abweichende Rechnungsanschrift"}</button>
          {abwAn && (
            <div className="grid gap-3 rounded-xl bg-neutral-50 p-3">
              <Feld label="Zusatz" v={rAnschrift.supplement} set={(v) => setRAnschrift({ ...rAnschrift, supplement: v })} />
              <Feld label="Straße" v={rAnschrift.street} set={(v) => setRAnschrift({ ...rAnschrift, street: v })} />
              <div className="grid grid-cols-3 gap-3"><Feld label="PLZ" v={rAnschrift.zip} set={(v) => setRAnschrift({ ...rAnschrift, zip: v })} /><div className="col-span-2"><Feld label="Ort" v={rAnschrift.city} set={(v) => setRAnschrift({ ...rAnschrift, city: v })} /></div></div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Feld label="Website" v={firma.website} set={(v) => setFirma({ ...firma, website: v })} />
            <Feld label="Instagram-Handle" v={firma.instagram} set={(v) => setFirma({ ...firma, instagram: v })} ph="@deinbetrieb" />
            <Feld label="Facebook-Seite" v={firma.facebook} set={(v) => setFirma({ ...firma, facebook: v })} />
          </div>
          <Feld label="Bestellnummer / Kostenstelle / Referenz (optional)" tid="firma-referenz" v={firma.referenz} set={(v) => setFirma({ ...firma, referenz: v })} />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <h2 className="font-head text-2xl font-bold">Ansprechpartner</h2>
          <div className="grid grid-cols-2 gap-3">
            <Feld label="Vorname *" tid="ap-vorname" v={ap.vorname} set={(v) => setAp({ ...ap, vorname: v })} />
            <Feld label="Nachname *" tid="ap-nachname" v={ap.nachname} set={(v) => setAp({ ...ap, nachname: v })} />
          </div>
          <Feld label="Position (optional)" v={ap.position} set={(v) => setAp({ ...ap, position: v })} />
          <Feld label="E-Mail *" tid="ap-email" v={ap.email} set={(v) => setAp({ ...ap, email: v })} />
          <Feld label="Telefon / Mobil *" tid="ap-telefon" v={ap.telefon} set={(v) => setAp({ ...ap, telefon: v })} />
          <label className="flex items-start gap-2 text-sm text-neutral-600 mt-2">
            <input type="checkbox" data-testid="cp-toggle" checked={cpAn} onChange={(e) => setCpAn(e.target.checked)} className="mt-1" />
            Ansprechpartner für Content-Zulieferung ist eine andere Person
          </label>
          {cpAn && (
            <div className="grid gap-3 rounded-xl bg-neutral-50 p-3">
              <Feld label="Name" tid="cp-name" v={cp.name} set={(v) => setCp({ ...cp, name: v })} />
              <Feld label="E-Mail" tid="cp-email" v={cp.email} set={(v) => setCp({ ...cp, email: v })} />
              <Feld label="Telefon" v={cp.telefon} set={(v) => setCp({ ...cp, telefon: v })} />
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4" data-testid="zahlungs-hinweis">
          <h2 className="font-head text-2xl font-bold">Zahlung &amp; Rechnung</h2>
          <div className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-3 text-sm text-neutral-700">
            <p>📧 Die Rechnung erhältst du per E-Mail an <b>{firma.rechnungs_email || "deine Rechnungs-E-Mail"}</b> — bei den einmaligen Paketen <b>einen Tag nach der Veröffentlichung</b>, beim Monatspaket jeweils <b>zu Beginn des Monats</b>.</p>
            <p>💶 Der Rechnungsbetrag ist innerhalb von <b>7 Tagen</b> nach Rechnungsdatum ohne Abzug per Überweisung zu begleichen.</p>
            <p className="text-[12px] text-neutral-400">Es werden keine Bankdaten abgefragt.</p>
          </div>
          <label className="flex items-start gap-2 text-sm text-neutral-600">
            <input type="checkbox" data-testid="sepa-interesse" checked={sepa} onChange={(e) => setSepa(e.target.checked)} className="mt-1" />
            Ich möchte künftig per SEPA-Lastschrift zahlen — bitte kontaktiert mich dazu.
          </label>
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4">
          <h2 className="font-head text-2xl font-bold">Zusammenfassung</h2>
          <div className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-2" data-testid="summary-box">
            {cart.map((c, i) => {
              const p = pakete.find((x) => x.key === c.paket_key);
              return <div key={i} className="flex justify-between text-sm"><span>{p?.name}{c.startmonat ? ` (ab ${c.startmonat})` : c.wunschdatum ? ` (${c.wunschdatum})` : c.event?.termin ? ` (${c.event.name}, ${c.event.termin})` : ""}</span><b>{eur(p?.preis_netto)}{p?.typ === "abo" ? "/Monat" : ""}</b></div>;
            })}
            <div className="border-t border-neutral-100 pt-2 text-sm space-y-1">
              <div className="flex justify-between"><span>Netto</span><span data-testid="summe-netto">{eur(summe.netto)}</span></div>
              <div className="flex justify-between"><span>USt ({ust} %)</span><span data-testid="summe-ust">{eur(summe.ust)}</span></div>
              <div className="flex justify-between font-bold"><span>Brutto</span><span data-testid="summe-brutto">{eur(summe.brutto)}</span></div>
              {aboGesamt !== summe.netto && <p className="text-[11px] text-neutral-400">Gesamtsumme über die Abo-Laufzeit: {eur(aboGesamt)} netto</p>}
            </div>
          </div>
          <textarea data-testid="anmerkungen" value={anmerkungen} onChange={(e) => setAnmerkungen(e.target.value)} placeholder="Anmerkungen (optional)" rows={2} className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm" />
          {[["agb", <>Ich habe die <a href="/nutzungsbedingungen" target="_blank" className="underline">AGB und Vertragshinweise</a> gelesen und akzeptiere sie. *</>],
            ["datenschutz", <>Ich habe die <a href="/datenschutz" target="_blank" className="underline">Datenschutzerklärung</a> zur Kenntnis genommen. *</>],
            ["rechte", "Ich willige ein, dass gelieferte Inhalte auf den Kanälen von Bruchköbel Beleben veröffentlicht und archiviert werden dürfen, und bestätige, über alle erforderlichen Nutzungs-, Bild- und Musikrechte zu verfügen. *"]].map(([k, label]) => (
            <label key={k} className="flex items-start gap-2 text-[13px] text-neutral-600">
              <input type="checkbox" data-testid={`consent-${k}`} checked={consents[k]} onChange={(e) => setConsents({ ...consents, [k]: e.target.checked })} className="mt-1" />
              <span>{label}</span>
            </label>
          ))}
          <p className="text-[12px] text-neutral-500">Mit Klick auf „Verbindlich buchen“ gibst du eine verbindliche Buchung ab. Es entstehen Kosten in Höhe von <b>{eur(summe.netto)} netto ({eur(summe.brutto)} brutto)</b>.</p>
          <button data-testid="verbindlich-buchen-btn" disabled={!stepOk || sending} onClick={submit}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-bold py-4 rounded-2xl text-base flex items-center justify-center gap-2">
            {sending && <Loader2 className="h-5 w-5 animate-spin" />} Verbindlich buchen
          </button>
        </div>
      )}

      {step < 5 && (
        <div className="flex gap-3 mt-8">
          {step > 0 && <button data-testid="wizard-back" onClick={() => setStep(step - 1)} className="flex items-center gap-1 px-5 py-3 rounded-xl border border-neutral-200 font-bold text-sm"><ChevronLeft className="h-4 w-4" /> Zurück</button>}
          <button data-testid="wizard-next" disabled={!stepOk} onClick={() => setStep(step + 1)} className="flex-1 bg-neutral-900 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-1">Weiter <ChevronRight className="h-4 w-4" /></button>
        </div>
      )}
      {step === 5 && <button data-testid="wizard-back" onClick={() => setStep(4)} className="mt-4 text-sm font-bold text-neutral-500 flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Zurück</button>}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#f7f7f8]">
      <div className="max-w-xl mx-auto px-4 py-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-400">Bruchköbel Beleben × Innovationshafen</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 mb-6">Digitaler Content für Bruchköbel</h1>
        {children}
        <p className="text-[10px] text-neutral-400 mt-10">Innovationshafen UG (haftungsbeschränkt) · Innerer Ring 1a · 63486 Bruchköbel · in Kooperation mit dem Citymanagement Bruchköbel</p>
      </div>
    </div>
  );
}

function Feld({ label, v, set, tid, ph }) {
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-500">{label}</label>
      <input data-testid={tid} value={v} onChange={(e) => set(e.target.value)} placeholder={ph}
        className="w-full mt-1 px-3 py-2.5 rounded-xl border border-neutral-200 text-sm bg-white" />
    </div>
  );
}
